// macadamia-app/src/macadamia-core/components/widgets/composite-text-widget/SubBlockEditor.tsx
import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ITextBlock, ISuggestion } from '../types';
import Toolbar from './Toolbar';
import ReaderToolbar from './ReaderToolbar';
import JurisprudenceModal from './JurisprudenceModal';
import JurisprudenceSearch from './JurisprudenceSearch';
import LegifranceModal from './legifrance/LegifranceModal';
import LegifranceSearch from './legifrance/LegifranceSearch';
import LinkPopover from './LinkPopover';
import JurisprudencePopover from './JurisprudencePopover';
import LegifrancePopover from './LegifrancePopover';
import AnnotationPopover from './AnnotationPopover';
import HALPopover from './HALPopover';
import HALModal from './hal/HALModal';
import HALDetailsModal from '../../../../features/hal/components/HALDetailsModal';
import { apiClient } from '../../../../utils/apiClient';
import { useJuridicalReferences, JuridicalReference } from '../../../contexts/JuridicalReferencesContext';

interface SubBlockEditorProps {
  textBlock: ITextBlock;
  onTextBlockChange: (newTextBlock: ITextBlock) => void;
  mode: 'editor' | 'reader';
  dossierId: string;
  onSuggestionCreated: (newSuggestion: ISuggestion) => void;
  compositeBlockId: string;
  userRole: string | null;
  chapterIndex: number;
  chapterTitle: string;
  isFirst?: boolean;
  placeholderClassName?: string;
}

interface decisionLink {
  title: string;
  number: string;
  description: string;
  theme: string[];
  url: string;
}

interface JurisprudenceData {
  reference: string;
  jurisdiction: string;
  chamber: string;
  number: string;
  ecli: string;
  formation: string;
  publication: string[];
  decisionDate: string;
  solution: string;
  summary: string;
  themes: string[];
  text: string;
  zones?: {
    introduction?: { start: number; end: number }[];
    expose?: { start: number; end: number }[];
    moyens?: { start: number; end: number }[];
    motivations?: { start: number; end: number }[];
    dispositif?: { start: number; end: number }[];
  };
  visa?: { title: string }[];
  contested?: {
    date: string;
    title: string;
    jurisdiction: string;
    chamber: string;
    solution: string;
    number: string;
  };
  rapprochements?: decisionLink[];
}

interface LinkPopoverState {
  isVisible: boolean;
  url: string;
  position: { top: number; left: number };
}

interface JurisprudencePopoverState {
  isVisible: boolean;
  referenceId: string;
  referenceText: string;
  position: { top: number; left: number };
  element: HTMLElement | null;
}

interface AnnotationPopoverState {
  isVisible: boolean;
  content: string;
  position: { top: number; left: number };
  element?: HTMLElement;
}

const SubBlockEditor: React.FC<SubBlockEditorProps> = ({
  textBlock,
  onTextBlockChange,
  mode,
  dossierId,
  onSuggestionCreated,
  compositeBlockId,
  userRole,
  chapterIndex,
  chapterTitle,
  isFirst,
  placeholderClassName
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [showPlaceholder, setShowPlaceholder] = useState(() => {
    // Initialiser à true uniquement si le bloc est vide
    return !textBlock.rawText || textBlock.rawText.trim() === '';
  });
  const [readerToolbarVisible, setReaderToolbarVisible] = useState(false);
  const [readerToolbarPosition, setReaderToolbarPosition] = useState({ top: 0, left: 0 });
  const [forceToolbarVisible, setForceToolbarVisible] = useState(false);
  const [forceReaderToolbarVisible, setForceReaderToolbarVisible] = useState(false);
  const { addReference, removeReference } = useJuridicalReferences();

  const [showModal, setShowModal] = useState(false);
  const [jurisprudenceData, setJurisprudenceData] = useState<JurisprudenceData | null>(null);
  const [isLoadingJurisprudence, setIsLoadingJurisprudence] = useState(false);
  const [jurisprudenceError, setJurisprudenceError] = useState<string | null>(null);
  const [followedJurisprudences, setFollowedJurisprudences] = useState<Set<string>>(new Set());
  const [linkPopover, setLinkPopover] = useState<LinkPopoverState>({
    isVisible: false,
    url: '',
    position: { top: 0, left: 0 },
  });

  const [jurisprudencePopover, setJurisprudencePopover] = useState<JurisprudencePopoverState>({
    isVisible: false,
    referenceId: '',
    referenceText: '',
    position: { top: 0, left: 0 },
    element: null,
  });

  const [halPopover, setHalPopover] = useState<{
    isVisible: boolean;
    halId: string;
    halTitle: string;
    position: { top: number; left: number };
    element: HTMLElement | null;
  }>({
    isVisible: false,
    halId: '',
    halTitle: '',
    position: { top: 0, left: 0 },
    element: null,
  });

  const [legifrancePopover, setLegifrancePopover] = useState<{
    isVisible: boolean;
    referenceId: string;
    code: string;
    section: string;
    article: string;
    position: { top: number; left: number };
    element: HTMLElement | null;
  }>({
    isVisible: false,
    referenceId: '',
    code: '',
    section: '',
    article: '',
    position: { top: 0, left: 0 },
    element: null,
  });

  const [annotationPopover, setAnnotationPopover] = useState<AnnotationPopoverState>({
    isVisible: false,
    content: '',
    position: { top: 0, left: 0 },
    element: undefined
  });

  const [showLegifranceModal, setShowLegifranceModal] = useState(false);
  const [legifranceModalData, setLegifranceModalData] = useState<{
    codeId?: string;
    sectionId?: string;
    articleId?: string;
  }>({});
  const [isLoadingLegifranceModal, setIsLoadingLegifranceModal] = useState(false);

  // Référence pour sauvegarder la sélection avant l'ouverture des modaux (useRef pour persister entre re-renders)
  const savedSelection = useRef<{
    range: Range;
    text: string;
  } | null>(null);

  // États pour stocker le texte sélectionné à passer aux modaux de recherche
  const [selectedTextForJurisprudence, setSelectedTextForJurisprudence] = useState<string>('');
  const [selectedTextForLegifrance, setSelectedTextForLegifrance] = useState<string>('');
  const [selectedTextForHAL, setSelectedTextForHAL] = useState<string>('');

  // États pour contrôler la visibilité des modaux de recherche
  const [jurisprudenceModalVisible, setJurisprudenceModalVisible] = useState(false);
  const [legifranceModalVisible, setLegifranceModalVisible] = useState(false);
  const [halModalVisible, setHalModalVisible] = useState(false);

  // État pour le modal de détails HAL (lecture)
  const [viewHALResource, setViewHALResource] = useState<any | null>(null);

  // Fonction utilitaire pour normaliser les espaces
  const normalizeSpaces = (text: string): string => {
    return text.replace(/\u00a0/g, ' ');
  };

  // Fonction pour sauvegarder la sélection actuelle
  const saveCurrentSelection = useCallback(() => {
    const selection = window.getSelection();

    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);

      // Vérifier que la sélection est dans notre éditeur
      const isInEditor = editorRef.current?.contains(range.commonAncestorContainer);

      if (isInEditor) {
        const clonedRange = range.cloneRange();
        const selectedText = selection.toString();
        savedSelection.current = {
          range: clonedRange,
          text: selectedText
        };
        return true;
      } else {
      }
    } else {
    }
    return false;
  }, []);

  // Handler pour ouvrir le modal de jurisprudence
  const handleOpenJurisprudenceModal = useCallback(() => {
    const saved = saveCurrentSelection();
    if (saved && savedSelection.current) {
      // Stocker le texte sélectionné pour la recherche
      setSelectedTextForJurisprudence(savedSelection.current.text);
      setForceToolbarVisible(false);
      setJurisprudenceModalVisible(true);
    }
  }, [saveCurrentSelection]);

  // Handler pour ouvrir le modal de Légifrance
  const handleOpenLegifranceModal = useCallback(() => {
    const saved = saveCurrentSelection();
    if (saved && savedSelection.current) {
      // Stocker le texte sélectionné pour la recherche
      setSelectedTextForLegifrance(savedSelection.current.text);
      setForceToolbarVisible(false);
      setLegifranceModalVisible(true);
    }
  }, [saveCurrentSelection]);

  // Handler pour ouvrir le modal HAL
  const handleOpenHALModal = useCallback(() => {
    const saved = saveCurrentSelection();
    if (saved && savedSelection.current) {
      // Stocker le texte sélectionné pour la recherche
      setSelectedTextForHAL(savedSelection.current.text);
      setForceToolbarVisible(false);
      setHalModalVisible(true);
    }
  }, [saveCurrentSelection]);

  // Handler de sélection pour jurisprudence
  const handleJurisprudenceSelect = useCallback((reference: string) => {
    handleAddEntity('jurisprudenceReference', { reference });
    setJurisprudenceModalVisible(false);
  }, []);

  // Handler de sélection pour Légifrance
  const handleLegifranceSelect = useCallback((reference: any) => {

    // Transformer les données du format {id, type, codeId?, sectionId?}
    // vers le format attendu {code, section, article, nature}
    const transformedReference: any = {
      nature: reference.type.toLowerCase(), // 'CODE' -> 'code', 'SECTION' -> 'section', 'ARTICLE' -> 'article'
    };

    if (reference.type === 'CODE') {
      transformedReference.code = reference.id;
    } else if (reference.type === 'SECTION') {
      transformedReference.code = reference.codeId;
      transformedReference.section = reference.id;
    } else if (reference.type === 'ARTICLE') {
      transformedReference.code = reference.codeId;
      transformedReference.section = reference.sectionId;
      transformedReference.article = reference.id;
    }

    handleAddEntity('legifranceReference', transformedReference);
    setLegifranceModalVisible(false);
  }, []);

  // Handler de sélection pour HAL
  const handleHALSelect = useCallback((ressource: any) => {
    handleAddEntity('halReference', { ressource });
    setHalModalVisible(false);
  }, []);

  // Fonction pour extraire les références juridiques du contenu HTML
  const extractReferencesFromHTML = (html: string): JuridicalReference[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const extractedReferences: JuridicalReference[] = [];

    const jurisprudenceElements = doc.querySelectorAll('.jurisprudence-highlight');
    jurisprudenceElements.forEach((element) => {
      const referenceId = element.getAttribute('data-reference-id');
      if (referenceId) {
        extractedReferences.push({
          id: referenceId,
          type: 'jurisprudence',
          text: element.textContent || '',
          blockId: compositeBlockId,
          chapterIndex,
          chapterTitle,
          details: {
            referenceId
          }
        });
      }
    });

    const legifranceElements = doc.querySelectorAll('.legifrance-highlight');
    legifranceElements.forEach((element) => {
      const codeId = element.getAttribute('data-legifrance-code');
      const sectionId = element.getAttribute('data-legifrance-section');
      const articleId = element.getAttribute('data-legifrance-article');

      if (codeId) {
        const refId = `${codeId}-${sectionId || ''}-${articleId || ''}`;
        extractedReferences.push({
          id: refId,
          type: 'legifrance',
          text: element.textContent || '',
          blockId: compositeBlockId,
          chapterIndex,
          chapterTitle,
          details: {
            codeId,
            sectionId: sectionId || undefined,
            articleId: articleId || undefined
          }
        });
      }
    });

    const halElements = doc.querySelectorAll('.hal-highlight');
    halElements.forEach((element) => {
      const halId = element.getAttribute('data-hal-id');
      if (halId) {
        extractedReferences.push({
          id: halId,
          type: 'hal',
          text: element.textContent || '',
          blockId: compositeBlockId,
          chapterIndex,
          chapterTitle,
          details: {
            halId
          }
        });
      }
    });

    return extractedReferences;
  };

  // Effets de nettoyage pour réinitialiser les textes sélectionnés à la fermeture des modaux
  useEffect(() => {
    if (!jurisprudenceModalVisible) {
      setSelectedTextForJurisprudence('');
    }
  }, [jurisprudenceModalVisible]);

  useEffect(() => {
    if (!legifranceModalVisible) {
      setSelectedTextForLegifrance('');
    }
  }, [legifranceModalVisible]);

  useEffect(() => {
    if (!halModalVisible) {
      setSelectedTextForHAL('');
    }
  }, [halModalVisible]);

  useEffect(() => {
    if (editorRef.current) {
      const normalizedText = normalizeSpaces(textBlock.rawText);

      if (editorRef.current.innerHTML !== normalizedText) {
        editorRef.current.innerHTML = normalizedText;
      }
      checkPlaceholderVisibility();

      if (textBlock.rawText) {
        const references = extractReferencesFromHTML(textBlock.rawText);
        references.forEach(ref => addReference(ref));
      }
    }
  }, [textBlock.rawText]);

  const handleContentChange = () => {
    if (mode !== 'editor') return;

    if (editorRef.current) {
      const newContent = normalizeSpaces(editorRef.current.innerHTML);
      const updatedReferences = extractReferencesFromHTML(newContent);
      updatedReferences.forEach(ref => addReference(ref));
      onTextBlockChange({ ...textBlock, rawText: newContent });
      checkPlaceholderVisibility();
    }
  };

  const handleFormat = (command: string, value?: string) => {
    if (mode !== 'editor') return;

    document.execCommand(command, false, value);
    handleContentChange();
  };

  const handleAddEntity = (type: string, data: any) => {
    if (mode !== 'editor') return;


    if (type === 'jurisprudenceReference') {
      // Utiliser la sélection sauvegardée si elle existe, sinon essayer la sélection courante
      let range: Range | null = null;
      let selectedText = '';


      if (savedSelection.current) {
        range = savedSelection.current.range;
        selectedText = savedSelection.current.text;
      } else {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          range = selection.getRangeAt(0);
          selectedText = selection.toString();
        } else {
        }
      }


      if (range && selectedText) {
        if (!editorRef.current?.contains(range.commonAncestorContainer)) {
          savedSelection.current = null;
          return;
        }
        const span = document.createElement('span');
        span.className = 'jurisprudence-highlight';
        span.dataset.referenceId = data.reference;
        span.textContent = selectedText;


        range.deleteContents();
        range.insertNode(span);

        savedSelection.current = null;

        handleContentChange();
      } else {
      }
    }

    if (type === 'link') {
      const selection = window.getSelection();

      if (selection && !selection.isCollapsed && editorRef.current?.contains(selection.anchorNode)) {

        document.execCommand('createLink', false, data.url);


        handleContentChange();
      } else {
        console.error('No valid text selected for creating a link');
      }
    }

    if (type === 'legifranceReference') {
      // Utiliser la sélection sauvegardée si elle existe, sinon essayer la sélection courante
      let range: Range | null = null;
      let selectedText = '';


      if (savedSelection.current) {
        range = savedSelection.current.range;
        selectedText = savedSelection.current.text;
      } else {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          range = selection.getRangeAt(0);
          selectedText = selection.toString();
        } else {
        }
      }


      if (range && selectedText) {
        if (!editorRef.current?.contains(range.commonAncestorContainer)) {
          savedSelection.current = null;
          return;
        }
        const span = document.createElement('span');

        span.className = 'legifrance-highlight';
        span.setAttribute('nature', 'code');

        switch (data.nature) {
          case 'code':
            span.setAttribute('data-legifrance-code', data.code);
            break;
          case 'section':
            span.setAttribute('data-legifrance-code', data.code);
            span.setAttribute('data-legifrance-section', data.section);
            break;
          case 'article':
            span.setAttribute('data-legifrance-code', data.code);
            span.setAttribute('data-legifrance-section', data.section);
            span.setAttribute('data-legifrance-article', data.article);
            break;
        }

        span.textContent = selectedText;


        range.deleteContents();
        range.insertNode(span);

        savedSelection.current = null;

        handleContentChange();
      }
    }

    if (type === 'halReference') {
      const selection = window.getSelection();
      let range: Range | null = null;
      let selectedText = '';

      if (savedSelection.current) {
        range = savedSelection.current.range;
        selectedText = savedSelection.current.text;
      } else if (selection && selection.rangeCount > 0) {
        range = selection.getRangeAt(0);
        selectedText = selection.toString();
      }

      if (range && selectedText) {
        if (!editorRef.current?.contains(range.commonAncestorContainer)) {
          savedSelection.current = null;
          return;
        }
        const span = document.createElement('span');
        span.className = 'hal-highlight';
        span.setAttribute('data-hal-id', data.ressource._id);
        span.setAttribute('data-hal-title', data.ressource.titre);
        span.textContent = selectedText;

        range.deleteContents();
        range.insertNode(span);

        savedSelection.current = null;
        handleContentChange();
      }
    }

    if (type === 'annotation') {
      const selection = window.getSelection();
      let range: Range | null = null;
      let selectedText = '';

      if (savedSelection.current) {
        range = savedSelection.current.range;
        selectedText = savedSelection.current.text;
      } else if (selection && selection.rangeCount > 0) {
        range = selection.getRangeAt(0);
        selectedText = selection.toString();
      }

      if (range && selectedText) {
        if (!editorRef.current?.contains(range.commonAncestorContainer)) {
          savedSelection.current = null;
          return;
        }
        const timestamp = Date.now();
        const annotationId = `annot-${timestamp}`;

        const span = document.createElement('span');
        span.className = 'annotation-marker';
        span.setAttribute('data-annotation-id', annotationId);
        // Utiliser le contenu fourni ou une valeur par défaut
        span.setAttribute('data-annotation-content', data.content || 'Nouvelle annotation');
        span.textContent = selectedText;

        range.deleteContents();
        range.insertNode(span);

        savedSelection.current = null;
        handleContentChange();

        // Plus besoin d'ouvrir le popover immédiatement car le contenu a déjà été saisi
      }
    }
  };

  const handleSelectionChange = () => {
    if (mode !== 'reader' || forceReaderToolbarVisible) return;

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && editorRef.current) {
      const range = selection.getRangeAt(0);

      if (!editorRef.current.contains(range.commonAncestorContainer)) {
        return;
      }

      const rect = range.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();
      const position = {
        top: rect.top - editorRect.top - 40,
        left: rect.left - editorRect.left,
      };
      setReaderToolbarVisible(true);
      setReaderToolbarPosition(position);
    } else if (!forceReaderToolbarVisible) {
      setReaderToolbarVisible(false);
    }
  };

  const handleReaderToolbarVisibilityChange = (isVisible: boolean) => {
    setForceReaderToolbarVisible(isVisible);
    if (isVisible) {
      setReaderToolbarVisible(true);
    }
  };

  const checkPlaceholderVisibility = () => {
    if (editorRef.current) {
      setShowPlaceholder(editorRef.current.innerHTML.trim() === '');
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = normalizeSpaces(event.clipboardData.getData('text/plain'));
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(text));
    handleContentChange();
  };

  const handleSuggestion = async (suggestionText: string, selectedText: string) => {

    if (!selectedText) {
      console.error('Pas de texte sélectionné');
      return;
    }

    // Calculer la position dans le texte
    let textOffset = 0;
    let textLength = selectedText.length;

    if (editorRef.current) {
      const fullText = editorRef.current.textContent || '';
      textOffset = fullText.indexOf(selectedText);

      if (textOffset === -1) {
        console.warn('Texte sélectionné non trouvé dans le bloc, utilisation offset 0');
        textOffset = 0;
      }
    }

    const requestBody = {
      dossierId: dossierId,
      blocId: compositeBlockId,
      content: suggestionText,
      position: {
        selectedText: selectedText,
        textOffset: textOffset,
        textLength: textLength
      }
    };


    try {
      const response = await apiClient(`/suggestions`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });


      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur response:', errorData);
        throw new Error(`Erreur lors de la création de la suggestion: ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      onSuggestionCreated(data);
    } catch (error) {
      console.error('Erreur:', error);
    }

    setReaderToolbarVisible(false);
  };

  // Mémoriser les handlers avec useCallback
  const handleJurisprudenceClick = useCallback(async (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const highlightEl = target.closest('.jurisprudence-highlight') as HTMLElement | null;
    if (highlightEl) {
      const referenceId = highlightEl.dataset.referenceId || '';

      setIsLoadingJurisprudence(true);
      setShowModal(true);
      setJurisprudenceError(null);

      try {
        const response = await apiClient(`/jurisprudences/decision?id=${referenceId}&resolve_references=true`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des données de la décision');
        }

        const decisionData = await response.json();

        setJurisprudenceData({
          reference: referenceId,
          jurisdiction: decisionData.jurisdiction || '',
          chamber: decisionData.chamber || '',
          number: decisionData.number || '',
          ecli: decisionData.ecli || '',
          formation: decisionData.formation || '',
          publication: decisionData.publication || [],
          decisionDate: decisionData.decision_date || '',
          solution: decisionData.solution || '',
          summary: decisionData.titlesAndSummaries?.[0]?.summary || '',
          themes: decisionData.themes || [],
          text: decisionData.text || '',
          zones: decisionData.zones || undefined,
          visa: decisionData.visa || [],
          rapprochements: decisionData.rapprochements || [],
          contested: decisionData.contested ? {
            date: decisionData.contested.date,
            title: decisionData.contested.title,
            jurisdiction: decisionData.contested.jurisdiction,
            chamber: decisionData.contested.chamber,
            solution: decisionData.contested.solution,
            number: decisionData.contested.number
          } : undefined,
        });

      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        setJurisprudenceError('Impossible de charger cette jurisprudence. Veuillez réessayer.');
      } finally {
        setIsLoadingJurisprudence(false);
      }

    }
  }, []);



  // Handler click pour le mode LECTURE (Modal Détails)
  const handleHALReaderClick = useCallback(async (event: MouseEvent) => {
    // En mode lecture, on veut juste ouvrir le modal de détails
    const target = event.target as HTMLElement;
    const highlightEl = target.closest('.hal-highlight') as HTMLElement | null;
    if (highlightEl) {
      event.stopPropagation(); // Éviter d'autres effets de bord

      const halId = highlightEl.getAttribute('data-hal-id');
      const halTitle = highlightEl.getAttribute('data-hal-title');

      if (halId) {
        // Affichage immédiat du modal avec les données disponibles (Optimistic UI)
        setViewHALResource({
          _id: halId,
          titre: halTitle || 'Chargement...',
          isLoading: true // Indicateur de chargement
        });

        try {
          const response = await apiClient(`/ressources/${halId}`);

          if (response.ok) {
            const data = await response.json();
            setViewHALResource(data);
          } else {
            console.error("Erreur chargement ressource HAL");
          }
        } catch (e) {
          console.error("Erreur fetch HAL", e);
        }
      }
    }
  }, []);

  const handleRemoveHAL = () => {
    if (halPopover.element) {
      const halElement = halPopover.element;
      const textNode = document.createTextNode(halElement.textContent || '');
      halElement.parentNode?.replaceChild(textNode, halElement);

      if (halPopover.halId) {
        removeReference(halPopover.halId);
      }

      setHalPopover(prev => ({ ...prev, isVisible: false }));
      handleContentChange();
    }
  };

  const handleJurisprudenceFollowChange = useCallback((reference: string, isFollowed: boolean) => {
    setFollowedJurisprudences(prev => {
      const newSet = new Set(prev);
      if (isFollowed) {
        newSet.add(reference);
      } else {
        newSet.delete(reference);
      }
      return newSet;
    });
  }, []);

  const handleLegifranceClick = useCallback(async (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const highlightEl = target.closest('.legifrance-highlight') as HTMLElement | null;
    if (highlightEl) {
      event.preventDefault();
      event.stopPropagation();


      const codeId = highlightEl.getAttribute('data-legifrance-code') ?? undefined;
      const sectionId = highlightEl.getAttribute('data-legifrance-section') ?? undefined;
      const articleId = highlightEl.getAttribute('data-legifrance-article') ?? undefined;


      setLegifranceModalData({
        codeId,
        sectionId,
        articleId
      });
      setIsLoadingLegifranceModal(true);
      setShowLegifranceModal(true);
    }
  }, []);

  const handleRemoveLink = () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const startContainer = range.startContainer;
        let linkElement = startContainer.parentElement;

        while (linkElement && linkElement.tagName !== 'A') {
          linkElement = linkElement.parentElement;
        }

        if (linkElement && linkElement.tagName === 'A') {
          const textNode = document.createTextNode(linkElement.textContent || '');
          linkElement.parentNode?.replaceChild(textNode, linkElement);
          setLinkPopover(prev => ({ ...prev, isVisible: false }));
          handleContentChange();
        }
      }
    }
  };



  const handleRemoveJurisprudence = () => {
    if (jurisprudencePopover.element) {
      const jurisprudenceElement = jurisprudencePopover.element;
      const textNode = document.createTextNode(jurisprudenceElement.textContent || '');
      jurisprudenceElement.parentNode?.replaceChild(textNode, jurisprudenceElement);

      // Supprimer la référence du contexte
      if (jurisprudencePopover.referenceId) {
        removeReference(jurisprudencePopover.referenceId);
      }

      setJurisprudencePopover(prev => ({ ...prev, isVisible: false }));
      handleContentChange();
    }
  };



  const handleRemoveLegifrance = () => {
    if (legifrancePopover.element) {
      const legifranceElement = legifrancePopover.element;
      const textNode = document.createTextNode(legifranceElement.textContent || '');
      legifranceElement.parentNode?.replaceChild(textNode, legifranceElement);

      // Supprimer la référence du contexte
      if (legifrancePopover.referenceId) {
        removeReference(legifrancePopover.referenceId);
      }

      setLegifrancePopover(prev => ({ ...prev, isVisible: false }));
      handleContentChange();
    }
  };

  /**
   * Calcule la position intelligente de la toolbar pour éviter les débordements
   * Retourne des coordonnées ABSOLUES par rapport au viewport (pour position: fixed)
   */
  const calculateSmartToolbarPosition = useCallback((
    selectionRect: DOMRect,
    toolbarWidth: number = 400 // Largeur par défaut estimée
  ): { top: number; left: number } => {
    const MARGIN = 20; // Marge par rapport aux bords du viewport
    const TOOLBAR_OFFSET_TOP = 50; // Distance au-dessus de la sélection
    const TOOLBAR_OFFSET_BOTTOM = 10; // Distance en-dessous si pas d'espace en haut

    // Position verticale par défaut : au-dessus de la sélection (coordonnées viewport)
    let top = selectionRect.top - TOOLBAR_OFFSET_TOP;

    // Si pas assez d'espace en haut, afficher en-dessous
    if (selectionRect.top < TOOLBAR_OFFSET_TOP + MARGIN) {
      top = selectionRect.bottom + TOOLBAR_OFFSET_BOTTOM;
    }

    // Position horizontale par défaut : centrée sur la sélection (coordonnées viewport)
    const selectionCenter = selectionRect.left + selectionRect.width / 2;
    let left = selectionCenter - toolbarWidth / 2;

    // Récupérer la largeur du viewport
    const viewportWidth = window.innerWidth;

    // Calculer la position de la toolbar dans le viewport
    const toolbarRightInViewport = left + toolbarWidth;

    // Si déborde à droite
    if (toolbarRightInViewport > viewportWidth - MARGIN) {
      // Aligner le bord droit de la toolbar avec le bord droit du viewport - marge
      left = viewportWidth - MARGIN - toolbarWidth;
    }

    // Si déborde à gauche
    if (left < MARGIN) {
      // Aligner le bord gauche de la toolbar avec la marge
      left = MARGIN;
    }

    return { top, left };
  }, []);

  // Centralized click handler for Editor Mode
  const handleEditorClick = useCallback((event: MouseEvent) => {
    if (mode !== 'editor') return;

    const target = event.target as HTMLElement;
    const editorRect = editorRef.current?.getBoundingClientRect();

    if (!editorRect) return;

    // --- HAL Reference ---
    const halEl = target.closest('.hal-highlight') as HTMLElement | null;
    if (halEl) {
      event.preventDefault();
      event.stopPropagation();
      const halId = halEl.getAttribute('data-hal-id') || '';
      const halTitle = halEl.getAttribute('data-hal-title') || '';
      const rect = halEl.getBoundingClientRect();

      setHalPopover({
        isVisible: true,
        halId,
        halTitle,
        position: {
          top: rect.bottom - editorRect.top,
          left: rect.left - editorRect.left,
        },
        element: halEl,
      });
      // Close others
      setJurisprudencePopover(prev => ({ ...prev, isVisible: false }));
      setLegifrancePopover(prev => ({ ...prev, isVisible: false }));
      setLinkPopover(prev => ({ ...prev, isVisible: false }));
      return;
    }

    // --- Jurisprudence Reference ---
    const juriEl = target.closest('.jurisprudence-highlight') as HTMLElement | null;
    if (juriEl) {
      event.preventDefault();
      event.stopPropagation();
      const referenceId = juriEl.getAttribute('data-reference-id') || '';
      const referenceText = juriEl.textContent || '';
      const rect = juriEl.getBoundingClientRect();

      setJurisprudencePopover({
        isVisible: true,
        referenceId,
        referenceText,
        position: {
          top: rect.bottom - editorRect.top,
          left: rect.left - editorRect.left,
        },
        element: juriEl,
      });
      // Close others
      setHalPopover(prev => ({ ...prev, isVisible: false }));
      setLegifrancePopover(prev => ({ ...prev, isVisible: false }));
      setLinkPopover(prev => ({ ...prev, isVisible: false }));
      return;
    }

    // --- Legifrance Reference ---
    const legiEl = target.closest('.legifrance-highlight') as HTMLElement | null;
    if (legiEl) {
      event.preventDefault();
      event.stopPropagation();
      const referenceId = legiEl.getAttribute('data-reference-id') || '';
      const code = legiEl.getAttribute('data-legifrance-code') || '';
      const section = legiEl.getAttribute('data-legifrance-section') || '';
      const article = legiEl.getAttribute('data-legifrance-article') || '';
      const rect = legiEl.getBoundingClientRect();

      setLegifrancePopover({
        isVisible: true,
        referenceId,
        code,
        section,
        article,
        position: {
          top: rect.bottom - editorRect.top,
          left: rect.left - editorRect.left,
        },
        element: legiEl,
      });
      // Close others
      setHalPopover(prev => ({ ...prev, isVisible: false }));
      setJurisprudencePopover(prev => ({ ...prev, isVisible: false }));
      setLinkPopover(prev => ({ ...prev, isVisible: false }));
      setAnnotationPopover(prev => ({ ...prev, isVisible: false }));
      return;
    }

    // --- Annotation ---
    const annotEl = target.closest('.annotation-marker') as HTMLElement | null;
    if (annotEl) {
      event.preventDefault();
      event.stopPropagation();
      const content = annotEl.getAttribute('data-annotation-content') || '';
      const rect = annotEl.getBoundingClientRect();

      setAnnotationPopover({
        isVisible: true,
        content,
        position: {
          top: rect.bottom,
          left: rect.left,
        },
        element: annotEl,
      });

      // Close others
      setHalPopover(prev => ({ ...prev, isVisible: false }));
      setJurisprudencePopover(prev => ({ ...prev, isVisible: false }));
      setLegifrancePopover(prev => ({ ...prev, isVisible: false }));
      setLinkPopover(prev => ({ ...prev, isVisible: false }));
      return;
    }

    // --- Simple Link (A tag) ---
    const linkEl = (target.closest('a') || (target.tagName === 'A' ? target : null)) as HTMLElement | null;
    if (linkEl) {
      event.preventDefault(); // Prevent navigation in editor
      const url = linkEl.getAttribute('href') || '';
      const rect = linkEl.getBoundingClientRect();

      setLinkPopover({
        isVisible: true,
        url,
        position: {
          top: rect.bottom - editorRect.top,
          left: rect.left - editorRect.left,
        },
      });
      // Close others
      setHalPopover(prev => ({ ...prev, isVisible: false }));
      setJurisprudencePopover(prev => ({ ...prev, isVisible: false }));
      setLegifrancePopover(prev => ({ ...prev, isVisible: false }));
      return;
    }

    // If clicking elsewhere in editor, close all popovers
    setHalPopover(prev => ({ ...prev, isVisible: false }));
    setJurisprudencePopover(prev => ({ ...prev, isVisible: false }));
    setLegifrancePopover(prev => ({ ...prev, isVisible: false }));
    setLinkPopover(prev => ({ ...prev, isVisible: false }));

  }, [mode]);

  // Centralized click handler for Reader Mode
  const handleReaderClick = useCallback((event: MouseEvent) => {
    if (mode !== 'reader') return;
    const target = event.target as HTMLElement;

    if (target.closest('.hal-highlight')) {
      handleHALReaderClick(event);
    } else if (target.closest('.jurisprudence-highlight')) {
      handleJurisprudenceClick(event);
    } else if (target.closest('.legifrance-highlight')) {
      handleLegifranceClick(event);
    } else if (target.closest('.annotation-marker')) {
      const annotationEl = target.closest('.annotation-marker') as HTMLElement;
      const content = annotationEl.getAttribute('data-annotation-content') || '';
      const rect = annotationEl.getBoundingClientRect();
      const editorRect = editorRef.current?.getBoundingClientRect();

      if (editorRect) {
        setAnnotationPopover({
          isVisible: true,
          content,
          position: {
            top: rect.top, // Coordonnées viewport directes car le popover est fixed
            left: rect.left,
          },
          element: annotationEl,
        });
      }
    }
  }, [mode, handleHALReaderClick, handleJurisprudenceClick, handleLegifranceClick]);

  // Attacher les event listeners en mode reader
  useEffect(() => {
    if (mode === 'reader' && editorRef.current) {

      const handleAllClicks = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        if (target.closest('.jurisprudence-highlight')) {
          handleJurisprudenceClick(event);
        }

        if (target.closest('.legifrance-highlight')) {
          handleLegifranceClick(event);
        }
      };

      editorRef.current.addEventListener('click', handleAllClicks, true);

      return () => {
        editorRef.current?.removeEventListener('click', handleAllClicks, true);
      };
    }
  }, [mode, handleJurisprudenceClick, handleLegifranceClick]);

  // Écouter les événements personnalisés de A4PageWrapper
  useEffect(() => {
    const handleCustomJurisprudenceClick = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { target } = customEvent.detail;

      const mockEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      Object.defineProperty(mockEvent, 'target', { value: target, writable: false });

      handleJurisprudenceClick(mockEvent);
    };

    const handleCustomLegifranceClick = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { target } = customEvent.detail;

      const mockEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      Object.defineProperty(mockEvent, 'target', { value: target, writable: false });

      handleLegifranceClick(mockEvent);
    };

    document.addEventListener('jurisprudence-reference-click', handleCustomJurisprudenceClick);
    document.addEventListener('legifrance-reference-click', handleCustomLegifranceClick);

    return () => {
      document.removeEventListener('jurisprudence-reference-click', handleCustomJurisprudenceClick);
      document.removeEventListener('legifrance-reference-click', handleCustomLegifranceClick);
    };
  }, [handleJurisprudenceClick, handleLegifranceClick]);

  useEffect(() => {
    if (mode === 'reader') {
      document.addEventListener('selectionchange', handleSelectionChange);
      return () => {
        document.removeEventListener('selectionchange', handleSelectionChange);
      };
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'editor') {
      const handleEditorSelectionChange = () => {
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed && editorRef.current && !forceToolbarVisible) {
          const range = selection.getRangeAt(0);
          if (!editorRef.current.contains(range.commonAncestorContainer)) {
            return;
          }

          const selectionRect = range.getBoundingClientRect();

          // Obtenir la largeur réelle de la toolbar si elle existe, sinon utiliser une estimation
          const toolbarWidth = toolbarRef.current?.offsetWidth || 400;

          // Calculer la position intelligente (coordonnées absolues viewport)
          const position = calculateSmartToolbarPosition(selectionRect, toolbarWidth);

          setToolbarVisible(true);
          setToolbarPosition(position);
        } else if (!forceToolbarVisible) {
          setToolbarVisible(false);
        }
      };

      document.addEventListener('selectionchange', handleEditorSelectionChange);
      return () => {
        document.removeEventListener('selectionchange', handleEditorSelectionChange);
      };
    }
  }, [mode, forceToolbarVisible, calculateSmartToolbarPosition]);

  /**
   * Recalculer la position de la toolbar après son rendu initial
   * pour obtenir sa largeur réelle et ajuster la position si nécessaire
   */
  useLayoutEffect(() => {
    if (toolbarVisible && toolbarRef.current && editorRef.current) {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        if (editorRef.current.contains(range.commonAncestorContainer)) {
          const selectionRect = range.getBoundingClientRect();
          const toolbarWidth = toolbarRef.current.offsetWidth;

          // Recalculer avec la largeur réelle (coordonnées absolues viewport)
          const adjustedPosition = calculateSmartToolbarPosition(selectionRect, toolbarWidth);

          // Mettre à jour seulement si la position a changé (éviter les boucles infinies)
          if (adjustedPosition.left !== toolbarPosition.left || adjustedPosition.top !== toolbarPosition.top) {
            setToolbarPosition(adjustedPosition);
          }
        }
      }
    }
  }, [toolbarVisible, calculateSmartToolbarPosition]);

  useEffect(() => {
    if (mode === 'editor' && editorRef.current) {
      editorRef.current.addEventListener('click', handleEditorClick);
      return () => {
        editorRef.current?.removeEventListener('click', handleEditorClick);
      };
    } else if (mode === 'reader' && editorRef.current) {
      editorRef.current.addEventListener('click', handleReaderClick);
      return () => {
        editorRef.current?.removeEventListener('click', handleReaderClick);
      };
    }
  }, [mode, handleEditorClick, handleReaderClick]);

  return (
    <div className="sub-block-editor relative">
      {mode === 'editor' && toolbarVisible && createPortal(
        <div
          ref={toolbarRef}
          className="bg-white shadow-lg rounded-lg"
          style={{
            position: 'fixed',
            top: `${toolbarPosition.top}px`,
            left: `${toolbarPosition.left}px`,
            zIndex: 9999,
          }}
        >
          <Toolbar
            onFormat={handleFormat}
            onAddEntity={handleAddEntity}
            mode={mode}
            setForceToolbarVisible={setForceToolbarVisible}
            onOpenJurisprudenceModal={handleOpenJurisprudenceModal}
            onOpenLegifranceModal={handleOpenLegifranceModal}
            onOpenHALModal={handleOpenHALModal}
          />
        </div>,
        document.body
      )}

      {/* Modaux de recherche rendus au même niveau que la Toolbar (mode éditeur) */}
      {mode === 'editor' && (
        <>
          <JurisprudenceSearch
            onSelect={handleJurisprudenceSelect}
            isVisible={jurisprudenceModalVisible}
            setIsVisible={setJurisprudenceModalVisible}
            initialSearchQuery={selectedTextForJurisprudence}
          />
          <LegifranceSearch
            onSelect={handleLegifranceSelect}
            isVisible={legifranceModalVisible}
            setIsVisible={setLegifranceModalVisible}
            initialSearchQuery={selectedTextForLegifrance}
          />
          <HALModal
            isVisible={halModalVisible}
            onClose={() => setHalModalVisible(false)}
            onSelect={handleHALSelect}
            initialSearchText={selectedTextForHAL}
          />
        </>
      )}


      {
        mode === 'reader' && (readerToolbarVisible || forceReaderToolbarVisible) && (
          <div
            className="absolute z-9"
            style={{
              top: `${readerToolbarPosition.top}px`,
              left: `${readerToolbarPosition.left}px`,
            }}
          >
            <ReaderToolbar
              onSuggestion={handleSuggestion}
              onVisibilityChange={handleReaderToolbarVisibilityChange}
            />
          </div>
        )
      }
      {
        showPlaceholder && mode === 'editor' && (
          <div
            className={`absolute flex pl-0 pointer-events-none text-gray-400 ${placeholderClassName || ''}`}
            style={{
              userSelect: 'none',
              top: 0,
              left: 0,
              right: 0,
              minHeight: '40px',
              paddingTop: '3px', // Alignement visuel avec le texte (approx line-height standard)
            }}
          >
            Veuillez renseigner votre texte...
          </div>
        )
      }
      {
        mode === 'reader' && showModal && (
          <JurisprudenceModal
            isVisible={showModal}
            onClose={() => setShowModal(false)}
            isLoading={isLoadingJurisprudence}
            error={jurisprudenceError}
            reference={jurisprudenceData?.reference || ''}
            jurisdiction={jurisprudenceData?.jurisdiction || ''}
            chamber={jurisprudenceData?.chamber || ''}
            number={jurisprudenceData?.number || ''}
            ecli={jurisprudenceData?.ecli || ''}
            formation={jurisprudenceData?.formation || ''}
            publication={jurisprudenceData?.publication || []}
            decisionDate={jurisprudenceData?.decisionDate || ''}
            solution={jurisprudenceData?.solution || ''}
            summary={jurisprudenceData?.summary || ''}
            themes={jurisprudenceData?.themes || []}
            text={jurisprudenceData?.text || ''}
            zones={jurisprudenceData?.zones}
            visa={jurisprudenceData?.visa || []}
            contested={jurisprudenceData?.contested}
            rapprochements={jurisprudenceData?.rapprochements || []}
            userRole={userRole}
            jurisprudenceFollowState={followedJurisprudences.has(jurisprudenceData?.reference || '')}
            onJurisprudenceFollowChange={handleJurisprudenceFollowChange}
          />
        )
      }

      {
        mode === 'reader' && showLegifranceModal && (
          <LegifranceModal
            isVisible={showLegifranceModal}
            onClose={() => setShowLegifranceModal(false)}
            isLoading={isLoadingLegifranceModal}
            codeId={legifranceModalData.codeId}
            sectionId={legifranceModalData.sectionId}
            articleId={legifranceModalData.articleId}
          />
        )
      }

      {
        mode === 'editor' && linkPopover.isVisible && (
          <LinkPopover
            url={linkPopover.url}
            onRemove={handleRemoveLink}
            position={linkPopover.position}
          />
        )
      }

      {
        mode === 'editor' && jurisprudencePopover.isVisible && (
          <JurisprudencePopover
            referenceId={jurisprudencePopover.referenceId}
            referenceText={jurisprudencePopover.referenceText}
            onRemove={handleRemoveJurisprudence}
            position={jurisprudencePopover.position}
          />
        )
      }

      {
        mode === 'editor' && legifrancePopover.isVisible && (
          <LegifrancePopover
            code={legifrancePopover.code}
            section={legifrancePopover.section}
            article={legifrancePopover.article}
            onRemove={handleRemoveLegifrance}
            position={legifrancePopover.position}
          />
        )
      }

      {
        mode === 'editor' && halPopover.isVisible && (
          /* Le overlay transparent pour fermer le popover si on clique ailleurs */
          <div
            className="fixed inset-0 z-40"
            onClick={() => setHalPopover(prev => ({ ...prev, isVisible: false }))}
          />
        )
      }

      {
        mode === 'editor' && halPopover.isVisible && (
          <HALPopover
            halId={halPopover.halId}
            halTitle={halPopover.halTitle}
            onRemove={handleRemoveHAL}
            position={halPopover.position}
          />
        )
      }

      {
        (mode === 'editor' || mode === 'reader') && annotationPopover.isVisible && (
          <React.Fragment>
            {mode === 'editor' && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => setAnnotationPopover(prev => ({ ...prev, isVisible: false }))}
              />
            )}
            {/* En mode reader, un clic global ferme souvent mais le composant AnnotationPopover gère son propre clic outside */}

            <AnnotationPopover
              isVisible={annotationPopover.isVisible}
              position={annotationPopover.position}
              content={annotationPopover.content}
              onClose={() => setAnnotationPopover(prev => ({ ...prev, isVisible: false }))}
              onSave={(newContent) => {
                if (annotationPopover.element) {
                  annotationPopover.element.setAttribute('data-annotation-content', newContent);
                  handleContentChange();
                }
                setAnnotationPopover(prev => ({ ...prev, isVisible: false, content: newContent }));
              }}
              onRemove={() => {
                if (annotationPopover.element) {
                  const parent = annotationPopover.element.parentNode;
                  if (parent) {
                    const text = annotationPopover.element.textContent || '';
                    const textNode = document.createTextNode(text);
                    parent.replaceChild(textNode, annotationPopover.element);
                    handleContentChange();
                  }
                }
                setAnnotationPopover(prev => ({ ...prev, isVisible: false }));
              }}
              readOnly={mode === 'reader'}
            />
          </React.Fragment>
        )
      }

      {/* 
        Si on est en mode "reader", le clic ouvre HALDetailsModal.
        C'est géré par l'état `viewHALResource` qui est set par `handleHALReaderClick`.
        Le composant HALDetailsModal est déjà rendu plus bas.
       */}

      <HALDetailsModal
        isVisible={!!viewHALResource}
        onClose={() => setViewHALResource(null)}
        resource={viewHALResource}
      />

      <div
        ref={editorRef}
        contentEditable={mode === 'editor'}
        onInput={handleContentChange}
        onBlur={handleContentChange}
        onPaste={handlePaste}
        className={`sub-block-content border border-transparent rounded relative ${mode === 'editor' ? 'editor-mode' : 'reader-mode'}`}
        style={{
          width: '100%',
          outline: 'none',
          minHeight: '40px',
          borderWidth: '1px',
          borderColor: 'rgba(0, 0, 0, 0)',
          textIndent: isFirst ? '40px' : '0px',
        }}
        data-block-id={compositeBlockId}
      />
    </div >
  );
};

export default SubBlockEditor;